import React, { useState, useEffect, useRef } from 'react';
import { API_URL } from '../config/api';

/**
 * FirmTagBar — tag chips with add/remove, for the firm header. Mirrors the
 * candidate header tagging UX but firm-specific.
 *
 * Tags are stored on the firm as custom_tags: { "<tagId>": {name,color} }.
 *  - available tag vocabulary: GET /api/internal/tags  (firm_tags table)
 *  - save applied tags:        PUT /api/firms/:firmId/tags
 *
 * Props: firmId, customTags (object), onChange(newTags) — parent persists
 * optimistically and calls the API; this component just renders + emits.
 */
export default function FirmTagBar({ firmId, customTags = {}, onRefresh }) {
    const [tags, setTags] = useState(customTags || {});
    const [allTags, setAllTags] = useState([]);
    const [showSearch, setShowSearch] = useState(false);
    const [query, setQuery] = useState('');
    const [saving, setSaving] = useState(false);
    const rootRef = useRef(null);
    const inputRef = useRef(null);

    const token = localStorage.getItem('authToken');

    // Fetch this firm's applied tags directly from our own endpoint, which
    // always returns custom_tags. We do NOT sync from the customTags prop,
    // because the firm-detail refetch (/firms/:id/detailed) may not include
    // custom_tags — relying on it would wipe a just-added tag on refresh.
    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API_URL}/firms/${firmId}/tags`, { headers: { Authorization: `Bearer ${token}` } });
                if (res.ok) {
                    const data = await res.json();
                    setTags(data && typeof data === 'object' ? data : {});
                }
            } catch (e) { console.error('Error loading firm applied tags:', e); }
        })();
        // eslint-disable-next-line
    }, [firmId]);

    // Load the firm tag vocabulary once (firm-entity tags, separate from candidate tags).
    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API_URL}/firms/tags/vocab`, { headers: { Authorization: `Bearer ${token}` } });
                if (res.ok) setAllTags(await res.json());
            } catch (e) { console.error('Error loading firm tags:', e); }
        })();
        // eslint-disable-next-line
    }, []);

    // Close search on outside click.
    useEffect(() => {
        const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) { setShowSearch(false); setQuery(''); } };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    useEffect(() => { if (showSearch && inputRef.current) inputRef.current.focus(); }, [showSearch]);

    const persist = async (newTags) => {
        const prev = tags;
        setTags(newTags); // optimistic
        setSaving(true);
        try {
            const res = await fetch(`${API_URL}/firms/${firmId}/tags`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ custom_tags: newTags }),
            });
            if (!res.ok) throw new Error('save failed');
            // Use the server's returned custom_tags as the source of truth.
            const saved = await res.json();
            if (saved && typeof saved === 'object') setTags(saved);
            onRefresh?.(); // refresh the rest of the detail view (harmless if it omits custom_tags)
        } catch (e) {
            setTags(prev); // rollback to what we had before
            alert('Failed to save tags');
        } finally { setSaving(false); }
    };

    const addTag = (tag) => {
        const next = { ...tags, [String(tag.id)]: { name: tag.name, color: tag.color || '#6B7280' } };
        persist(next);
        setShowSearch(false); setQuery('');
    };

    // Create a new firm tag on the fly, then apply it.
    const createAndAdd = async () => {
        const name = query.trim();
        if (!name) return;
        try {
            const res = await fetch(`${API_URL}/firms/tags/vocab`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ name }),
            });
            if (!res.ok) throw new Error('create failed');
            const tag = await res.json();
            setAllTags((prev) => prev.some(t => t.id === tag.id) ? prev : [...prev, tag]);
            addTag(tag);
        } catch (e) { alert('Failed to create tag'); }
    };

    const removeTag = (key) => {
        const next = { ...tags };
        delete next[key];
        persist(next);
    };

    const entries = Object.entries(tags);
    const available = allTags.filter(t => !tags[String(t.id)] && t.name.toLowerCase().includes(query.toLowerCase()));

    return (
        <div ref={rootRef} className="flex flex-wrap items-center gap-1.5">
            {entries.map(([key, value]) => {
                const name = (typeof value === 'object' && value) ? value.name || key : key;
                const color = (typeof value === 'object' && value && value.color) ? value.color : '#6B7280';
                return (
                    <span key={key}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{ backgroundColor: `${color}1f`, color }}>
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                        {name}
                        <button onClick={() => removeTag(key)} className="ml-0.5 opacity-60 hover:opacity-100" title="Remove tag">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </span>
                );
            })}

            {showSearch ? (
                <div className="relative">
                    <div className="flex items-center gap-1 bg-white border border-gray-300 rounded-full px-2 py-0.5 shadow-sm">
                        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                        <input ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search tags..." className="w-24 text-xs border-none focus:outline-none focus:ring-0 bg-transparent" />
                        {saving && <div className="animate-spin rounded-full h-3 w-3 border border-gray-400 border-t-transparent" />}
                        <button onClick={() => { setShowSearch(false); setQuery(''); }} className="text-gray-400 hover:text-gray-600">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>
                    {available.length > 0 ? (
                        <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto py-1">
                            {available.map((tag) => (
                                <button key={tag.id} onClick={() => addTag(tag)}
                                    className="w-full px-3 py-1.5 text-left text-sm flex items-center gap-2 hover:bg-emerald-50">
                                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color || '#6B7280' }} />
                                    <span className="truncate">{tag.name}</span>
                                    {tag.usage_count > 0 && <span className="text-xs text-gray-400 ml-auto">{tag.usage_count}</span>}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                            {query.trim() ? (
                                <button onClick={createAndAdd} className="w-full px-3 py-1.5 text-left text-sm flex items-center gap-2 hover:bg-emerald-50 text-emerald-700">
                                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                    Create "{query.trim()}"
                                </button>
                            ) : (
                                <div className="px-3 py-2 text-xs text-gray-400">Type to search or create a tag</div>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <button onClick={() => setShowSearch(true)}
                    className="flex items-center gap-1 px-2 py-0.5 text-xs text-gray-500 hover:text-gray-700 border border-dashed border-gray-300 rounded-full hover:border-gray-400 transition-colors">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    Add tag
                </button>
            )}
        </div>
    );
}
